import { useState, useEffect } from 'react';

/**
 * Hook to fetch and manage data for a list of association configurations.
 * 
 * @param {string} parentId - The unique ID of the parent record.
 * @param {Object} parentData - The full parent record data (used for preloaded data).
 * @param {Array} associationConfigs - List of active association configuration objects.
 * @returns {Object} { data, loading, errors, refresh }
 */
export const useAssociationData = (parentId, parentData, associationConfigs) => {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!parentId || associationConfigs.length === 0) return;

        const fetchAllAssociations = async () => {
            const newData = {};
            const newLoading = {};
            const newErrors = {};

            // Initialize loading states and handle pre-loaded data
            associationConfigs.forEach(assoc => {
                if (assoc.getPreloadedData) {
                    // Custom function to extract/combine pre-loaded data (e.g., hosts + recruiters)
                    newData[assoc.key] = assoc.getPreloadedData(parentData) || [];
                    newLoading[assoc.key] = false;
                    newErrors[assoc.key] = null;
                } else if (assoc.dataField) {
                    // Use pre-loaded data directly from parentData (e.g., analysts from job response)
                    let records = parentData?.[assoc.dataField];

                    // Handle both array and single object (e.g., vendor is object, not array)
                    if (!Array.isArray(records)) {
                        records = records ? [records] : [];
                    }

                    // Apply transform function if provided (e.g., convert sessions to job list)
                    if (assoc.transformData) {
                        records = assoc.transformData(parentData?.[assoc.dataField]);
                    }

                    newData[assoc.key] = records;
                    newLoading[assoc.key] = false;
                    newErrors[assoc.key] = null;
                } else if (assoc.getFetchEndpoint) {
                    // Need to fetch from endpoint
                    newLoading[assoc.key] = true;
                    newErrors[assoc.key] = null;
                }
            });
            setLoading(newLoading);
            setData(newData);

            // Fetch data for associations that need it
            await Promise.all(
                associationConfigs.map(async (assoc) => {
                    if (assoc.dataField || !assoc.getFetchEndpoint) return;

                    try {
                        const endpoint = await assoc.getFetchEndpoint(parentId, parentData);

                        if (!endpoint) {
                            newData[assoc.key] = [];
                            newLoading[assoc.key] = false;
                            return;
                        }

                        const response = await fetch(endpoint);
                        if (!response.ok) {
                            if (response.status === 404) {
                                newData[assoc.key] = [];
                                newLoading[assoc.key] = false;
                                return;
                            }
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }

                        const responseData = await response.json();
                        // Handle both array responses and single object responses
                        let records = Array.isArray(responseData)
                            ? responseData
                            : (responseData.results || (responseData.id || responseData.name ? [responseData] : []));

                        // Apply filter function if provided (e.g., filter contacts by client)
                        if (assoc.filterFn) {
                            records = assoc.filterFn(records, parentData);
                        }

                        newData[assoc.key] = records;
                    } catch (error) {
                        console.error(`Error fetching ${assoc.name}:`, error);
                        if (error.message && !error.message.includes('404')) {
                            newErrors[assoc.key] = error.message || 'Failed to fetch data';
                        } else {
                            newErrors[assoc.key] = null;
                        }
                        newData[assoc.key] = [];
                    } finally {
                        newLoading[assoc.key] = false;
                    }
                })
            );

            setData(newData);
            setLoading(newLoading);
            setErrors(newErrors);
        };

        fetchAllAssociations();
    }, [parentId, associationConfigs]);

    return { data, loading, errors };
};
